import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { TrustBar } from "@/components/TrustBar";
import { ServiceCard } from "@/components/ServiceCard";
import { TestimonialCard } from "@/components/TestimonialCard";
import { FAQAccordion } from "@/components/FAQAccordion";
import { CTABlock } from "@/components/CTABlock";
import { CalculatorCTA } from "@/components/CalculatorCTA";
import { RelatedArticles } from "@/components/RelatedArticles";
import { towns } from "@/data/towns";
import { homeFaqs } from "@/data/homeFaqs";
import { buildHomeJsonLd } from "@/seo/jsonld";
import { testimonials } from "@/data/testimonials";
import heroImg from "@/assets/photos/home-suburban-roof.webp";
import { GOOGLE_REVIEWS_URL, YELP_URL } from "@/data/reviewLinks";
import {
  Wrench,
  Hammer,
  Search,
  CloudLightning,
  Layers,
  ShieldCheck,
  BadgeCheck,
  Award,
  Star,
  MapPin,
  ArrowRight,
} from "lucide-react";

const PHONE = "717-725-7170";

const roofingServices = [
  {
    title: "Roof Repair",
    description:
      "Fast, lasting repairs for roof leaks, storm damage, missing shingles, and failed flashing.",
    href: "/services/roof-repair-lancaster-pa/",
    icon: <Wrench />,
    features: ["Leak detection & repair", "Flashing & valley repair", "Emergency tarping"],
  },
  {
    title: "Roof Replacement",
    description:
      "Complete tear-off and installation of a properly ventilated, code-compliant roof.",
    href: "/services/roofing-company-lancaster-pa/",
    icon: <Hammer />,
    features: ["Architectural shingles", "Full tear-off & underlayment", "Manufacturer-spec install"],
  },
  {
    title: "Roof Inspections",
    description:
      "Thorough, documented roof inspections with honest written findings.",
    href: "/services/roofing-inspection-lancaster-pa/",
    icon: <Search />,
    features: ["Storm damage documentation", "Pre-sale inspections", "Clear written report"],
  },
  {
    title: "Storm Damage Repair",
    description:
      "Wind, hail, and tree-damage assessment with full insurance claim support.",
    href: "/services/roofing-storm-damage-lancaster-pa/",
    icon: <CloudLightning />,
    features: ["Emergency tarping", "Insurance claim help", "Detailed assessment"],
  },
  {
    title: "Metal Roofing",
    description:
      "Standing seam and metal shingle systems built to last for decades.",
    href: "/services/metal-roofing-lancaster-pa/",
    icon: <Layers />,
    features: ["Standing seam", "Metal shingles", "Long-lasting protection"],
  },
  {
    title: "Flat & Rubber Roofing",
    description:
      "EPDM, TPO, and modified bitumen systems for flat and low-slope roofs.",
    href: "/services/rubber-roofing-installation-lancaster-pa/",
    icon: <ShieldCheck />,
    features: ["EPDM & TPO membranes", "Seam & flashing detail", "Low-slope expertise"],
  },
];

const whyChoose = [
  {
    icon: <BadgeCheck className="h-6 w-6 text-primary" />,
    title: "Licensed & Insured",
    desc: "Pennsylvania Home Improvement Contractor license #PA034872.",
  },
  {
    icon: <Award className="h-6 w-6 text-primary" />,
    title: "GAF Certified",
    desc: "Factory-certified to install GAF shingle systems to specification.",
  },
  {
    icon: <ShieldCheck className="h-6 w-6 text-primary" />,
    title: "A+ BBB Accredited",
    desc: "Accredited with an A+ rating from the Better Business Bureau.",
  },
  {
    icon: <Star className="h-6 w-6 text-primary fill-current" />,
    title: "5.0 Star Rated",
    desc: "Rated 5.0 stars on Google and Yelp from local homeowners.",
  },
  {
    icon: <MapPin className="h-6 w-6 text-primary" />,
    title: "Local Since 2007",
    desc: "Based in East Petersburg, serving Lancaster County for 18+ years.",
  },
  {
    icon: <Wrench className="h-6 w-6 text-primary" />,
    title: "No High-Pressure Sales",
    desc: "Honest recommendations and clear written pricing, never a sales pitch.",
  },
];

const lancasterTowns = towns.filter((town) => town.county === "Lancaster County");

export default function Home() {
  const schemas = buildHomeJsonLd();

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>Roofing Contractors in Lancaster PA | Graystone Roofing & Siding</title>
        <meta
          name="description"
          content="Graystone Roofing & Siding offers roof repair, roof replacement, siding, and seamless gutters across Lancaster County, PA. Free estimates. Call 717-725-7170."
        />
        <link rel="canonical" href="https://lancasterparoofing.com/" />
        <meta property="og:title" content="Roofing Contractors in Lancaster PA | Graystone Roofing & Siding" />
        <meta
          property="og:description"
          content="Roof repair, roof replacement, and roof inspections for homeowners across Lancaster County, PA. GAF certified, A+ BBB, locally owned since 2007."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lancasterparoofing.com/" />
        <meta property="og:image" content="https://lancasterparoofing.com/opengraph.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Roofing Contractors in Lancaster PA | Graystone Roofing & Siding" />
        <meta
          name="twitter:description"
          content="Roof repair, roof replacement, and roof inspections across Lancaster County, PA. GAF certified, A+ BBB, locally owned since 2007."
        />
        <meta name="twitter:image" content="https://lancasterparoofing.com/opengraph.jpg" />
        {schemas.map((schema, idx) => (
          <script key={idx} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>

      {/* Hero */}
      <section className="relative bg-secondary overflow-hidden">
        <div className="absolute inset-0 z-0">
          <picture className="block h-full w-full">
            <img
              src={heroImg}
              alt="Residential home with a freshly installed shingle roof in Lancaster County, PA"
              width={1280}
              height={822}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </picture>
          <div className="absolute inset-0 bg-secondary/80 mix-blend-multiply" />
          <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/80 to-transparent" />
        </div>

        <div className="container mx-auto px-4 relative z-10 py-16 md:py-24">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-6 leading-tight">
              Lancaster PA Roofing Company
            </h1>
            <p className="text-lg md:text-xl text-secondary-foreground/90 mb-8 max-w-2xl leading-relaxed">
              Graystone Roofing &amp; Siding provides roof repair, roof replacement, and roof
              inspections for homeowners across Lancaster County, PA. GAF certified, A+ BBB
              accredited, and locally owned since 2007.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                href="/contact-graystone-roofing-siding/"
                className="bg-primary text-primary-foreground px-6 py-3 rounded font-bold hover:bg-primary/90 transition-colors shadow-sm"
              >
                Request a Free Quote
              </Link>
              <a
                href={`tel:+1${PHONE.replace(/-/g, "")}`}
                className="bg-white/10 text-white border border-white/20 px-6 py-3 rounded font-bold hover:bg-white/20 transition-colors backdrop-blur-sm"
              >
                Call {PHONE}
              </a>
            </div>
          </div>
        </div>
      </section>

      <TrustBar />

      {/* Entity summary / intro */}
      <section className="py-16 px-4 bg-background">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">
            Your Local Roofing Contractor in Lancaster, PA
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Graystone Roofing &amp; Siding is a locally owned roofing company based in East
            Petersburg, serving homeowners throughout Lancaster County, PA. Since 2007 we have
            installed and repaired asphalt shingle, metal, and flat roofs across the region. As a
            GAF certified, A+ BBB accredited contractor rated 5.0 stars on{" "}
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:no-underline">Google</a>{" "}
            and{" "}
            <a href={YELP_URL} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:no-underline">Yelp</a>, we
            deliver honest recommendations, quality installation, and clear written pricing on every
            roof repair and roof replacement.
          </p>
          <div className="mt-6">
            <Link
              href="/about-graystone-roofing-siding-lancaster/"
              className="text-primary font-bold hover:underline inline-flex items-center gap-2"
            >
              Learn more about Graystone Roofing
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Roof Repair + Roof Replacement */}
      <section className="py-16 px-4 bg-muted">
        <div className="container mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
          <article className="bg-card border border-border rounded-lg p-8 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-4">
              Roof Repair in Lancaster, PA
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              From a single roof leak to widespread storm damage, our team repairs roofs across
              Lancaster County quickly and correctly. We diagnose and fix missing or curling
              shingles, failed flashing around chimneys and skylights, valley leaks, and damaged
              underlayment, then explain exactly what your roof needs in plain language. When a sound
              repair will protect your home, we will tell you, instead of pushing a full replacement
              you do not need.
            </p>
            <Link
              href="/services/roof-repair-lancaster-pa/"
              className="text-primary font-bold hover:underline inline-flex items-center gap-2"
            >
              Explore roof repair
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </article>

          <article className="bg-card border border-border rounded-lg p-8 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-4">
              Roof Replacement in Lancaster, PA
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              When a roof has reached the end of its life, we install a complete, properly ventilated
              roofing system built for Pennsylvania weather. Choose from{" "}
              <Link
                href="/services/shingle-roofing-lancaster-pa/"
                className="text-primary hover:underline"
              >
                architectural asphalt shingles
              </Link>
              ,{" "}
              <Link
                href="/services/metal-roofing-lancaster-pa/"
                className="text-primary hover:underline"
              >
                standing seam and metal roofing
              </Link>
              , or flat and rubber (EPDM/TPO) systems, all installed to manufacturer specifications
              with new underlayment, ice and water barrier, and flashing. As a GAF certified
              contractor, we back every roof replacement with quality materials and careful
              workmanship.
            </p>
            <Link
              href="/services/roofing-company-lancaster-pa/"
              className="text-primary font-bold hover:underline inline-flex items-center gap-2"
            >
              Explore roof replacement
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </article>
        </div>
      </section>

      {/* Roofing Services */}
      <section className="py-20 px-4 bg-background">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
              Roofing Services
            </h2>
            <p className="text-lg text-muted-foreground">
              Graystone handles every part of your roof, from quick repairs to full replacements,
              inspections, and storm damage restoration.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {roofingServices.map((svc) => (
              <ServiceCard
                key={svc.title}
                title={svc.title}
                description={svc.description}
                href={svc.href}
                icon={svc.icon}
                features={svc.features}
              />
            ))}
          </div>

          <p className="text-center text-muted-foreground mt-10">
            Graystone also installs{" "}
            <Link
              href="/services/siding-contractor-lancaster-pa/"
              className="text-primary font-medium hover:underline"
            >
              vinyl and specialty siding
            </Link>{" "}
            and{" "}
            <Link
              href="/services/gutter-installation-lancaster-pa-york-pa/"
              className="text-primary font-medium hover:underline"
            >
              seamless gutters
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-20 px-4 bg-muted">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
              Why Choose Graystone Roofing
            </h2>
            <p className="text-lg text-muted-foreground">
              A local, accountable roofing contractor that homeowners across Lancaster County trust
              for honest work and lasting results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item) => (
              <div
                key={item.title}
                className="bg-card border border-border rounded-lg p-6 shadow-sm flex items-start gap-4"
              >
                <div className="p-3 rounded-full bg-primary/10 shrink-0">{item.icon}</div>
                <div>
                  <h3 className="font-serif font-bold text-card-foreground text-lg mb-1">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-background">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-muted-foreground">
              Read reviews from homeowners across Lancaster and York counties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((testimonial, idx) => (
              <TestimonialCard key={idx} testimonial={testimonial} />
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              href="/roofing-siding-york-lancaster-pa-testimonials/"
              className="inline-flex bg-card text-card-foreground border border-border px-6 py-3 rounded font-medium hover:bg-muted transition-colors"
            >
              View All Testimonials
            </Link>
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-20 px-4 bg-secondary text-secondary-foreground">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
              Areas We Serve in Lancaster County, PA
            </h2>
            <p className="text-lg text-secondary-foreground/80">
              Based in East Petersburg, we provide roof repair and roof replacement throughout
              Lancaster County and nearby communities, including:
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {lancasterTowns.map((town) => (
              <Link
                key={town.slug}
                href={`/service-areas/roofing-contractor-${town.slug}-pa/`}
                className="text-center py-2 px-3 rounded hover:bg-white/10 transition-colors text-sm font-medium"
              >
                {town.name}
              </Link>
            ))}
          </div>

          <div className="text-center mt-10 space-y-4">
            <p className="text-secondary-foreground/80">
              We also serve York, Chester, Berks, Dauphin, and Cumberland counties.
            </p>
            <Link
              href="/service-areas/"
              className="text-primary hover:text-white transition-colors font-bold underline"
            >
              View all service areas
            </Link>
          </div>
        </div>
      </section>

      {/* From the Learning Center */}
      <RelatedArticles
        slugs={[
          "should-i-repair-patch-or-replace-my-roof",
          "what-causes-a-roof-to-leak",
          "the-top-5-benefits-of-vinyl-siding",
        ]}
        heading="From the Graystone Help Hub"
        intro="Practical roofing and siding guides from our team to help Lancaster County homeowners make confident, well-informed decisions."
      />

      {/* FAQ */}
      <section className="py-20 px-4 bg-background">
        <div className="container mx-auto">
          <FAQAccordion faqs={homeFaqs} title="Roofing FAQ" />
        </div>
      </section>

      <CalculatorCTA />

      <CTABlock
        title="Request Your Free Roofing Estimate"
        subtitle="Call Graystone Roofing at 717-725-7170 or schedule online for a free, no-pressure roof inspection and written estimate."
      />
    </div>
  );
}
